<template>
  <div class="sales-order-wizard">
    <nav class="sales-order-wizard__rail" :aria-label="$t('sales_orders.wizard.steps')">
      <ol class="sales-order-wizard__steps">
        <li v-for="(step, index) in steps" :key="step.id">
          <button
            type="button"
            class="sales-order-wizard__step"
            :class="{
              'sales-order-wizard__step--current': index === currentIndex,
              'sales-order-wizard__step--done': index < currentIndex,
            }"
            :disabled="!canVisit(index)"
            :aria-current="index === currentIndex ? 'step' : undefined"
            @click="goTo(index)"
          >
            <span class="sales-order-wizard__step-marker">
              <BaseIcon v-if="index < currentIndex" name="check" size="12" />
              <template v-else>{{ index + 1 }}</template>
            </span>
            <span class="sales-order-wizard__step-text">
              <span class="sales-order-wizard__step-title">{{ step.title }}</span>
              <span v-if="step.summary" class="sales-order-wizard__step-summary">
                {{ step.summary }}
              </span>
            </span>
          </button>
        </li>
      </ol>
    </nav>

    <main class="sales-order-wizard__main">
      <header v-if="!configuring" class="sales-order-wizard__header">
        <p class="sales-order-wizard__eyebrow">
          {{
            $t('sales_orders.wizard.step_of', { current: currentIndex + 1, total: steps.length })
          }}
        </p>
        <h3 class="sales-order-wizard__title">{{ currentStep.heading }}</h3>
        <p v-if="currentStep.hint" class="sales-order-wizard__hint">{{ currentStep.hint }}</p>
      </header>

      <div
        class="sales-order-wizard__content"
        :class="{ 'sales-order-wizard__content--flush': configuring }"
      >
        <!-- Customer -->
        <div v-show="currentStep.id === 'customer'" class="sales-order-wizard__stack">
          <SearchableLeadSelect
            id="sales_order_lead"
            v-model="leadId"
            :label="$t('sales_orders.customer')"
            :placeholder="$t('sales_orders.customer_placeholder')"
            @lead-selected="onLeadSelected"
          >
            <template #label>
              <label for="sales_order_lead">{{ $t('sales_orders.customer') }}</label>
            </template>
          </SearchableLeadSelect>

          <div v-if="lead" class="sales-order-wizard__customer">
            <strong>{{ leadName }}</strong>
            <span v-if="lead.email">{{ lead.email }}</span>
            <span v-if="lead.phone">{{ lead.phone }}</span>
            <span v-if="leadAddressLine">{{ leadAddressLine }}</span>
          </div>

          <p v-if="onUseProposal" class="sales-order-wizard__alt">
            {{ $t('sales_orders.wizard.from_proposal_question') }}
            <button type="button" @click="onUseProposal">
              {{ $t('sales_orders.wizard.from_proposal_action') }}
            </button>
          </p>
        </div>

        <!-- Products -->
        <SalesOrderLinesEditor
          v-show="currentStep.id === 'products'"
          v-model="lines"
          @priced="pricing = $event"
          @configuring="configuring = $event"
        />

        <!-- Project -->
        <div v-show="currentStep.id === 'project'" class="sales-order-wizard__stack">
          <div class="sales-order-wizard__pair">
            <TextInput
              id="sales_order_reference"
              v-model="project.customer_reference"
              :label="$t('projects.customer_reference')"
              :placeholder="$t('projects.customer_reference_placeholder')"
              :info="$t('projects.customer_reference_help')"
            />
            <div />
          </div>
          <TextAreaInput
            id="sales_order_description"
            v-model="project.description"
            :label="$t('projects.description')"
            :placeholder="$t('projects.description_placeholder')"
            :rows="2"
          />

          <div>
            <h4 class="sales-order-wizard__subheading">
              {{ $t('projects.installation_address') }}
            </h4>
            <CheckboxInput
              id="sales_order_use_lead_address"
              :label="$t('projects.use_lead_address')"
              :model-value="useLeadAddress"
              :disabled="!leadAddress"
              @update:model-value="useLeadAddress = $event"
            />
            <p v-if="useLeadAddress && leadAddressLine" class="sales-order-wizard__muted">
              {{ leadAddressLine }}
            </p>
            <AddressInput
              v-if="!useLeadAddress"
              id="sales_order_address"
              v-model="project.installation_address"
            />
          </div>

          <div class="sales-order-wizard__pair">
            <DateInput
              id="sales_order_start"
              v-model="project.expected_start_date"
              :label="$t('projects.expected_start')"
            />
            <DateInput
              id="sales_order_completion"
              v-model="project.expected_completion_date"
              :label="$t('projects.expected_completion')"
            />
          </div>

          <TextAreaInput
            id="sales_order_notes"
            v-model="project.internal_notes"
            :label="$t('projects.internal_notes')"
            :placeholder="$t('projects.internal_notes_placeholder')"
            :rows="3"
          />
        </div>

        <!-- Review -->
        <div v-show="currentStep.id === 'review'" class="sales-order-wizard__review">
          <dl class="sales-order-wizard__facts">
            <div>
              <dt>{{ $t('sales_orders.customer') }}</dt>
              <dd>{{ leadName || '—' }}</dd>
            </div>
            <div>
              <dt>{{ $t('projects.installation_address') }}</dt>
              <dd>{{ installationAddressLine || '—' }}</dd>
            </div>
            <div v-if="project.customer_reference">
              <dt>{{ $t('projects.customer_reference') }}</dt>
              <dd>{{ project.customer_reference }}</dd>
            </div>
            <div v-if="project.expected_start_date || project.expected_completion_date">
              <dt>{{ $t('sales_orders.wizard.planning') }}</dt>
              <dd>
                {{ formatDate(project.expected_start_date) || '…' }}
                –
                {{ formatDate(project.expected_completion_date) || '…' }}
              </dd>
            </div>
          </dl>

          <ul class="sales-order-wizard__review-lines">
            <li v-for="item in reviewItems" :key="item.key">
              <div>
                <strong>{{ item.title }}</strong>
                <p v-if="item.details">{{ item.details }}</p>
              </div>
              <span class="sales-order-wizard__review-qty">{{ item.quantity }}×</span>
              <span class="sales-order-wizard__review-amount">
                {{ item.total != null ? formatCurrency(item.total) : '…' }}
              </span>
            </li>
          </ul>

          <p class="sales-order-wizard__muted">{{ $t('sales_orders.wizard.confirm_hint') }}</p>
        </div>
      </div>

      <footer v-if="!configuring" class="sales-order-wizard__footer">
        <p v-if="stepError" class="sales-order-wizard__error">{{ stepError }}</p>
        <div class="sales-order-wizard__footer-actions">
          <Btn
            v-if="currentIndex > 0"
            variant="outlined"
            type="button"
            :text="$t('common.back')"
            @click="goTo(currentIndex - 1)"
          />
          <Btn
            v-if="!isLastStep"
            variant="primary"
            type="button"
            :text="$t('common.next')"
            @click="next"
          />
          <Btn
            v-else
            variant="primary"
            type="button"
            :text="$t('sales_orders.confirm_and_start')"
            :loading="saving"
            :disabled="pricing.loading || !!pricing.error"
            @click="submit"
          />
        </div>
      </footer>
    </main>

    <aside class="sales-order-wizard__summary" :aria-label="$t('sales_orders.wizard.summary')">
      <h4 class="sales-order-wizard__summary-title">{{ $t('sales_orders.wizard.summary') }}</h4>

      <div class="sales-order-wizard__summary-block">
        <span class="sales-order-wizard__summary-label">{{ $t('sales_orders.customer') }}</span>
        <span>{{ leadName || $t('sales_orders.wizard.no_customer') }}</span>
      </div>

      <div class="sales-order-wizard__summary-block">
        <span class="sales-order-wizard__summary-label">{{ $t('sales_orders.products') }}</span>
        <ul v-if="reviewItems.length" class="sales-order-wizard__summary-lines">
          <li v-for="item in reviewItems" :key="item.key">
            <span>{{ item.quantity }}× {{ item.title }}</span>
            <span>{{ item.total != null ? formatCurrency(item.total) : '…' }}</span>
          </li>
        </ul>
        <span v-else class="sales-order-wizard__muted">
          {{ $t('sales_orders.wizard.no_lines') }}
        </span>
      </div>

      <div class="sales-order-wizard__summary-total">
        <span>{{ $t('sales_orders.total_incl') }}</span>
        <strong v-if="pricing.result && !pricing.loading">
          {{ formatCurrency(pricing.result.total_amount) }}
        </strong>
        <span v-else-if="pricing.loading" class="sales-order-wizard__muted">
          {{ $t('sales_orders.pricing') }}
        </span>
        <strong v-else>{{ formatCurrency(0) }}</strong>
      </div>
      <p v-if="pricing.error" class="sales-order-wizard__error">{{ pricing.error }}</p>
      <p class="sales-order-wizard__summary-hint">{{ $t('sales_orders.price_server_hint') }}</p>
    </aside>
  </div>
</template>

<script>
  import { computed, getCurrentInstance, ref, watch } from 'vue';
  import { salesOrderService } from '@/services/salesOrderService';
  import { hasSalesOrderLines } from '@/utils/salesOrderLines';
  import TextInput from '@/components/inputs/TextInput.vue';
  import TextAreaInput from '@/components/inputs/TextAreaInput.vue';
  import DateInput from '@/components/inputs/DateInput.vue';
  import CheckboxInput from '@/components/inputs/CheckboxInput.vue';
  import AddressInput from '@/components/inputs/AddressInput.vue';
  import SearchableLeadSelect from '@/components/inputs/SearchableLeadSelect.vue';
  import SalesOrderLinesEditor from '@/components/orders/SalesOrderLinesEditor.vue';
  import Btn from '@/components/partials/Btn.vue';
  import BaseIcon from '@shared/components/BaseIcon.vue';

  const EMPTY_ADDRESS = {
    street: '',
    houseNumber: '',
    postalCode: '',
    city: '',
    country: 'Nederland',
  };

  function addressOf(lead) {
    if (!lead) return null;
    const addr = typeof lead.address === 'object' && lead.address ? lead.address : {};
    const address = {
      street: addr.street || lead.street || '',
      houseNumber: addr.houseNumber || lead.house_number || '',
      postalCode: addr.postalCode || lead.postal_code || lead.zipcode || '',
      city: addr.city || lead.city || '',
      country: addr.country || lead.country || 'Nederland',
    };
    return address.street || address.city ? address : null;
  }

  function addressLine(address) {
    if (!address) return '';
    const street = [address.street, address.houseNumber].filter(Boolean).join(' ');
    const place = [address.postalCode, address.city].filter(Boolean).join(' ');
    return [street, place].filter(Boolean).join(', ');
  }

  /**
   * Sell without a quote: customer, configured products, project details,
   * review. Confirming records the sales order and starts the project. With a
   * deal the customer is fixed and the deal is won.
   */
  export default {
    name: 'SalesOrderWizard',

    components: {
      TextInput,
      TextAreaInput,
      DateInput,
      CheckboxInput,
      AddressInput,
      SearchableLeadSelect,
      SalesOrderLinesEditor,
      Btn,
      BaseIcon,
    },

    props: {
      deal: { type: Object, default: null },
      onSubmit: { type: Function, required: true },
      onUseProposal: { type: Function, default: null },
    },

    setup(props) {
      const instance = getCurrentInstance();
      const $t = instance.appContext.config.globalProperties.$t;

      const dealLead = props.deal?.lead || null;
      const lead = ref(dealLead);
      const leadId = ref(dealLead?.id || props.deal?.lead_id || null);
      const lines = ref([]);
      const pricing = ref({ result: null, loading: false, error: '' });
      const configuring = ref(false);
      const saving = ref(false);
      const stepError = ref('');
      const currentIndex = ref(0);
      const furthestIndex = ref(0);

      const project = ref({
        customer_reference: '',
        description: '',
        installation_address: { ...EMPTY_ADDRESS },
        expected_start_date: null,
        expected_completion_date: null,
        internal_notes: '',
      });

      const leadAddress = computed(() => addressOf(lead.value));
      const leadAddressLine = computed(() => addressLine(leadAddress.value));
      const useLeadAddress = ref(true);
      watch(
        leadAddress,
        (address) => {
          if (!address) useLeadAddress.value = false;
        },
        { immediate: true }
      );

      const leadName = computed(() => {
        const l = lead.value;
        if (!l) return '';
        return (
          l.name ||
          l.company_name ||
          [l.first_name, l.last_name].filter(Boolean).join(' ') ||
          l.email ||
          ''
        );
      });

      const installationAddressLine = computed(() =>
        useLeadAddress.value
          ? leadAddressLine.value
          : addressLine(project.value.installation_address)
      );

      const productLines = computed(() => lines.value.filter((l) => l.kind === 'product'));

      const reviewItems = computed(() => {
        const result = pricing.value.result;
        const items = result?.items || [];
        const products = productLines.value.map((line, ordinal) => {
          const group = items.filter(
            (item) => item.source === 'configurator' && item.product_index === ordinal
          );
          return {
            key: line.key,
            title: line.title,
            details: line.details,
            quantity: line.quantity,
            total:
              !pricing.value.loading && group.length
                ? group.reduce((sum, item) => sum + item.quantity * item.unit_price, 0)
                : null,
          };
        });
        const manual = lines.value
          .filter((l) => l.kind === 'manual' && String(l.description || '').trim())
          .map((line) => ({
            key: line.key,
            title: line.description,
            details: '',
            quantity: line.quantity,
            total: (Number(line.quantity) || 0) * (Number(line.unit_price) || 0),
          }));
        return [...products, ...manual];
      });

      const productCountLabel = computed(() => {
        const count = reviewItems.value.length;
        return count ? $t('sales_orders.wizard.line_count', { count }) : '';
      });

      const steps = computed(() => {
        const all = [
          {
            id: 'customer',
            title: $t('sales_orders.customer'),
            heading: $t('sales_orders.wizard.customer_heading'),
            hint: $t('sales_orders.wizard.customer_hint'),
            summary: leadName.value,
          },
          {
            id: 'products',
            title: $t('sales_orders.products'),
            heading: $t('sales_orders.wizard.products_heading'),
            hint: $t('sales_orders.wizard.products_hint'),
            summary: productCountLabel.value,
          },
          {
            id: 'project',
            title: $t('sales_orders.wizard.project'),
            heading: $t('sales_orders.wizard.project_heading'),
            hint: $t('sales_orders.wizard.project_hint'),
            summary: furthestIndex.value > 2 ? installationAddressLine.value : '',
          },
          {
            id: 'review',
            title: $t('sales_orders.wizard.review'),
            heading: $t('sales_orders.wizard.review_heading'),
            hint: '',
            summary: '',
          },
        ];
        return props.deal ? all.filter((step) => step.id !== 'customer') : all;
      });

      const currentStep = computed(() => steps.value[currentIndex.value]);
      const isLastStep = computed(() => currentIndex.value === steps.value.length - 1);

      const validateStep = (step) => {
        if (step.id === 'customer' && !leadId.value) return $t('sales_orders.customer_required');
        if (step.id === 'products' && !hasSalesOrderLines(lines.value)) {
          return $t('sales_orders.line_required');
        }
        return '';
      };

      const canVisit = (index) => index <= furthestIndex.value;

      const goTo = (index) => {
        if (index < 0 || index >= steps.value.length || !canVisit(index)) return;
        stepError.value = '';
        currentIndex.value = index;
      };

      const next = () => {
        const error = validateStep(currentStep.value);
        stepError.value = error;
        if (error) return;
        currentIndex.value += 1;
        furthestIndex.value = Math.max(furthestIndex.value, currentIndex.value);
      };

      // Earlier answers can change; jumping ahead is only allowed up to the
      // first step that is no longer valid.
      watch(
        [leadId, lines],
        () => {
          stepError.value = '';
          const firstInvalid = steps.value.findIndex((step) => validateStep(step));
          if (firstInvalid >= 0 && furthestIndex.value > firstInvalid) {
            furthestIndex.value = Math.max(firstInvalid, currentIndex.value);
          }
        },
        { deep: true }
      );

      const onLeadSelected = (selected) => {
        lead.value = selected || null;
        leadId.value = selected?.id || null;
        useLeadAddress.value = Boolean(addressOf(selected));
      };

      const submit = async () => {
        const invalid = steps.value.find((step) => validateStep(step));
        if (invalid) {
          stepError.value = validateStep(invalid);
          return;
        }
        saving.value = true;
        stepError.value = '';
        try {
          const salesOrder = await salesOrderService.create({
            dealId: props.deal?.id || null,
            leadId: props.deal ? null : leadId.value,
            lines: lines.value,
            project: {
              description: project.value.description || null,
              customer_reference: project.value.customer_reference || null,
              installation_address: useLeadAddress.value
                ? null
                : project.value.installation_address,
              expected_start_date: project.value.expected_start_date,
              expected_completion_date: project.value.expected_completion_date,
              internal_notes: project.value.internal_notes || null,
            },
          });
          props.onSubmit(salesOrder);
        } catch (error) {
          stepError.value = error?.response?.data?.error || $t('sales_orders.create_failed');
        } finally {
          saving.value = false;
        }
      };

      const formatCurrency = (amount) =>
        new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR' }).format(
          Number(amount) || 0
        );

      const formatDate = (value) =>
        value ? new Date(value).toLocaleDateString('nl-NL', { dateStyle: 'medium' }) : '';

      return {
        steps,
        currentIndex,
        currentStep,
        isLastStep,
        canVisit,
        goTo,
        next,
        submit,
        saving,
        stepError,
        configuring,
        lead,
        leadId,
        leadName,
        leadAddress,
        leadAddressLine,
        useLeadAddress,
        installationAddressLine,
        onLeadSelected,
        lines,
        pricing,
        project,
        reviewItems,
        formatCurrency,
        formatDate,
      };
    },
  };
</script>

<style scoped lang="scss">
  .sales-order-wizard {
    display: grid;
    grid-template-columns: 13rem minmax(0, 1fr) 17rem;
    height: min(78vh, 48rem);
    margin: -1rem -1.5rem;

    @include lg-down {
      grid-template-columns: minmax(0, 1fr);
      grid-template-rows: auto minmax(0, 1fr);
      height: min(85vh, 52rem);
      margin: -1rem;
    }

    &__rail {
      padding: 1.5rem 1rem;
      border-right: 1px solid var(--slate-200);
      background: var(--slate-50);

      @include lg-down {
        padding: 0.75rem 1rem;
        border-right: 0;
        border-bottom: 1px solid var(--slate-200);
        overflow-x: auto;
      }
    }

    &__steps {
      list-style: none;
      margin: 0;
      padding: 0;
      display: flex;
      flex-direction: column;
      gap: 0.25rem;

      @include lg-down {
        flex-direction: row;
        gap: 0.5rem;
      }
    }

    &__step {
      display: flex;
      align-items: flex-start;
      gap: 0.625rem;
      width: 100%;
      padding: 0.625rem 0.625rem;
      border: 0;
      border-radius: 0.5rem;
      background: none;
      text-align: left;
      cursor: pointer;
      color: var(--slate-500);

      &:disabled {
        cursor: default;
      }

      &:not(:disabled) {
        @include hover {
          background: var(--slate-100);
        }
      }

      &--current {
        background: white;
        color: var(--slate-900);
        box-shadow: 0 1px 2px rgb(15 23 42 / 0.06);

        @include hover {
          background: white;
        }
      }

      &--done {
        color: var(--slate-700);
      }

      &:focus-visible {
        outline: 2px solid var(--brand-500);
        outline-offset: 1px;
      }
    }

    &__step-marker {
      display: inline-flex;
      flex-shrink: 0;
      align-items: center;
      justify-content: center;
      width: 1.375rem;
      height: 1.375rem;
      border: 1px solid var(--slate-300);
      border-radius: 999px;
      font-size: 0.75rem;
      font-weight: 600;
      background: white;

      .sales-order-wizard__step--current & {
        border-color: var(--brand-600);
        background: var(--brand-600);
        color: white;
      }

      .sales-order-wizard__step--done & {
        border-color: var(--emerald-600);
        background: var(--emerald-600);
        color: white;
      }
    }

    &__step-text {
      display: flex;
      flex-direction: column;
      min-width: 0;
      padding-top: 0.0625rem;
    }

    &__step-title {
      font-size: 0.875rem;
      font-weight: 500;
      white-space: nowrap;
    }

    &__step-summary {
      margin-top: 0.125rem;
      font-size: 0.75rem;
      color: var(--slate-500);
      @include ellipsis(2);

      @include lg-down {
        display: none;
      }
    }

    &__main {
      display: flex;
      flex-direction: column;
      min-width: 0;
      min-height: 0;
      padding: 1.5rem 2rem 0;

      @include lg-down {
        padding: 1rem 1rem 0;
      }
    }

    &__header {
      padding-bottom: 1.25rem;
    }

    &__eyebrow {
      margin: 0 0 0.25rem;
      font-size: 0.75rem;
      font-weight: 500;
      color: var(--slate-500);
    }

    &__title {
      margin: 0;
      font-size: 1.25rem;
      font-weight: 600;
      color: var(--slate-900);
    }

    &__hint {
      margin: 0.375rem 0 0;
      max-width: 60ch;
      font-size: 0.875rem;
      color: var(--slate-600);
    }

    &__content {
      flex: 1 1 auto;
      min-height: 0;
      overflow-y: auto;
      padding: 0 0.25rem 1.5rem 0;

      &--flush {
        display: flex;
        flex-direction: column;
        overflow: hidden;
        padding-bottom: 1.25rem;
      }
    }

    &__stack {
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
      max-width: 40rem;
    }

    &__pair {
      display: grid;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: 1rem;

      @include sm-down {
        grid-template-columns: 1fr;
      }
    }

    &__subheading {
      margin: 0.5rem 0 0.5rem;
      font-size: 0.875rem;
      font-weight: 600;
      color: var(--slate-700);
    }

    &__customer {
      display: flex;
      flex-direction: column;
      gap: 0.125rem;
      padding: 0.875rem 1rem;
      border-radius: 0.5rem;
      background: var(--slate-50);
      font-size: 0.875rem;
      color: var(--slate-600);

      strong {
        color: var(--slate-900);
        font-size: 0.9375rem;
      }
    }

    &__alt {
      margin: 1.5rem 0 0;
      font-size: 0.875rem;
      color: var(--slate-600);

      button {
        padding: 0;
        border: 0;
        background: none;
        font: inherit;
        font-weight: 500;
        color: var(--brand-600);
        cursor: pointer;

        @include hover {
          text-decoration: underline;
        }
      }
    }

    &__muted {
      margin: 0;
      font-size: 0.8125rem;
      color: var(--slate-500);
    }

    &__review {
      display: flex;
      flex-direction: column;
      gap: 1.5rem;
      max-width: 44rem;
    }

    &__facts {
      display: grid;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: 1rem 2rem;
      margin: 0;

      dt {
        font-size: 0.75rem;
        color: var(--slate-500);
      }

      dd {
        margin: 0.125rem 0 0;
        font-size: 0.9375rem;
        color: var(--slate-900);
      }
    }

    &__review-lines {
      list-style: none;
      margin: 0;
      padding: 0;
      border-top: 1px solid var(--slate-200);

      li {
        display: grid;
        grid-template-columns: minmax(0, 1fr) 3rem 7rem;
        gap: 1rem;
        padding: 0.875rem 0;
        border-bottom: 1px solid var(--slate-200);
        font-size: 0.875rem;
      }

      strong {
        color: var(--slate-900);
      }

      p {
        margin: 0.25rem 0 0;
        font-size: 0.8125rem;
        line-height: 1.5;
        color: var(--slate-600);
        white-space: pre-line;
      }
    }

    &__review-qty {
      color: var(--slate-600);
      text-align: right;
    }

    &__review-amount {
      text-align: right;
      font-weight: 600;
      font-variant-numeric: tabular-nums;
      color: var(--slate-900);
    }

    &__footer {
      display: flex;
      align-items: center;
      gap: 1rem;
      padding: 1rem 0 1.25rem;
      border-top: 1px solid var(--slate-200);
    }

    &__footer-actions {
      display: flex;
      gap: 0.5rem;
      margin-left: auto;
    }

    &__error {
      margin: 0;
      font-size: 0.8125rem;
      color: var(--red-600);
    }

    &__summary {
      display: flex;
      flex-direction: column;
      gap: 1.25rem;
      padding: 1.5rem 1.25rem;
      border-left: 1px solid var(--slate-200);
      overflow-y: auto;

      @include lg-down {
        display: none;
      }
    }

    &__summary-title {
      margin: 0;
      font-size: 0.75rem;
      font-weight: 600;
      letter-spacing: 0.04em;
      text-transform: uppercase;
      color: var(--slate-500);
    }

    &__summary-block {
      display: flex;
      flex-direction: column;
      gap: 0.25rem;
      font-size: 0.875rem;
      color: var(--slate-800);
    }

    &__summary-label {
      font-size: 0.75rem;
      color: var(--slate-500);
    }

    &__summary-lines {
      list-style: none;
      margin: 0;
      padding: 0;
      display: flex;
      flex-direction: column;
      gap: 0.375rem;

      li {
        display: flex;
        justify-content: space-between;
        gap: 0.75rem;

        span:first-child {
          min-width: 0;
          @include ellipsis(2);
        }

        span:last-child {
          flex-shrink: 0;
          font-variant-numeric: tabular-nums;
        }
      }
    }

    &__summary-total {
      display: flex;
      justify-content: space-between;
      align-items: baseline;
      margin-top: auto;
      padding-top: 1rem;
      border-top: 1px solid var(--slate-200);
      font-size: 0.875rem;
      color: var(--slate-700);

      strong {
        font-size: 1.25rem;
        font-variant-numeric: tabular-nums;
        color: var(--slate-900);
      }
    }

    &__summary-hint {
      margin: 0;
      font-size: 0.75rem;
      line-height: 1.5;
      color: var(--slate-500);
    }
  }
</style>
